import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";

const BASE = "/v2/manifest-gate/pickup-desk";

/** Raise the Delhivery B2B pickup for one shipment, pre-filled from the shipment and its pickup location. */
export default function PickupRaiseDialog({ shipment, onClose, onRaised }) {
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose; // the parent re-renders every few seconds; keep the effect from re-running and wiping the form

  useEffect(() => {
    let live = true;
    api.get(`${BASE}/${shipment.id}`)
      .then((r) => { if (live) { setSt(r.data); setF({ ...r.data.form }); } })
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not load pickup details"); closeRef.current(); });
    return () => { live = false; };
  }, [shipment.id]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`${BASE}/${shipment.id}/raise`, {
        pickup_date: f.pickup_date, pickup_slot: f.pickup_slot, contact_person: f.contact_person,
        contact_phone: f.contact_phone, box_count: Number(f.box_count) || 1,
      });
      toast.success(`Pickup raised${r.data.delhivery_pur_number ? ` - Delhivery #${r.data.delhivery_pur_number}` : ""}`);
      await onRaised();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(d && typeof d === "object" ? d : { title: "Pickup not raised", message: d || "Something went wrong - please try again." });
    }
    setBusy(false);
  };

  const toggleAuto = async (on) => {
    try { const r = await api.put("/v2/manifest-gate/settings", { auto_pickup: on }); setSt((p) => ({ ...p, auto_pickup: r.data.auto_pickup })); toast.success(on ? "Pickups will now be raised automatically when the LR arrives" : "Automatic pickup is off"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change the setting"); }
  };

  const inp = "w-full border rounded-lg px-2.5 py-1.5 text-sm";
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pickup-raise-dialog">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-slate-800">Raise Delhivery pickup - {shipment.order_id}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        {!f ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : st.raised ? (
          <div className="p-4 text-sm flex gap-2 text-emerald-700"><CheckCircle2 className="w-5 h-5 shrink-0" /> Already raised: Delhivery #{st.pur?.delhivery_pur_number || "-"} for {st.pur?.pickup_date} ({st.pur?.slot_time}).</div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="text-xs bg-slate-50 border rounded-lg p-2.5">
              <div className="font-semibold text-slate-700">Pickup location: {f.location || "-"}</div>
              <div className="text-slate-500">{f.address}</div>
            </div>
            {f.note && <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2">{f.note}</div>}
            {f.requested_date && <div className="text-[11px] text-slate-500">Requested at booking: {f.requested_date} {f.requested_slot || ""}</div>}
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-slate-600">Date<input type="date" className={inp} value={f.pickup_date} onChange={(e) => setF({ ...f, pickup_date: e.target.value })} /></label>
              <label className="text-xs text-slate-600">Slot
                <select className={inp} value={f.pickup_slot} onChange={(e) => setF({ ...f, pickup_slot: e.target.value })}>
                  <option value="MORNING">Morning 09:00 - 13:00</option>
                  <option value="EVENING">Evening 14:00 - 18:00</option>
                </select>
              </label>
              <label className="text-xs text-slate-600">Contact person<input className={inp} value={f.contact_person || ""} onChange={(e) => setF({ ...f, contact_person: e.target.value })} /></label>
              <label className="text-xs text-slate-600">Contact phone<input className={inp} value={f.contact_phone || ""} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></label>
              <label className="text-xs text-slate-600">Boxes<input type="number" min="1" className={inp} value={f.box_count} onChange={(e) => setF({ ...f, box_count: e.target.value })} /></label>
            </div>
            <p className="text-[11px] text-slate-500">Delhivery's same-day cutoff is 2 PM. If this location already has a pickup for the day, this LR is added to it.</p>
            {err && (
              <div className="flex gap-2 text-xs rounded p-2 border bg-red-50 border-red-200 text-red-800" data-testid="pickup-error">
                <AlertTriangle className="w-4 h-4 shrink-0" /><span><b>{err.title}: </b>{err.message}</span>
              </div>
            )}
            {user?.role === "admin" && (
              <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" className="mt-0.5 accent-brand-600" checked={!!st.auto_pickup} onChange={(e) => toggleAuto(e.target.checked)} />
                <span>Admin: raise the pickup <b>automatically</b> the moment the LR arrives (uses the slot chosen at booking).</span>
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-500">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="pickup-raise-submit" className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-40">{busy ? "Sending to Delhivery..." : "Raise pickup"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
